import React, { useEffect } from "react";

import { Option, Category } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { ReportProps } from "../../..";
import BadgeBookingStatus from "../../../../../../../../../components/Generic/BadgeBookingStatus";
import renderSharedReference from "../../../../../../../../../components/Generic/SharedReference";
import { MoreOptions } from "../../../../../../../../../components/MoreOptionsComponent/MoreOptions";
import { useAppState } from "../../../../../../../../../context/StateContext";
import { Booking } from "../../../../../../../../../models/booking";
import { Company } from "../../../../../../../../../models/company";
import { Port } from "../../../../../../../../../models/port";
import { PurchaseOrder } from "../../../../../../../../../models/purchase.order";
import { Supplier } from "../../../../../../../../../models/supplier";
import PortService from "../../../../../../../../../services/port.service";
import PurchaseOrderService from "../../../../../../../../../services/purchase.order.service";
import ShipmentBookingService from "../../../../../../../../../services/shipment.booking.service";
import SupplierService from "../../../../../../../../../services/supplier.service";
import { ColumnWithKey } from "../../../../../../../../../types";
import {
  capitalizeFirstLetter,
  capitalizeFirstLetterAllWorlds,
} from "../../../../../../../../../util/capitalizeFirstLetter";
import { BookingModes } from "../../../../../../../../../util/enums";
import { formatDateWithoutTimeZone } from "../../../../../../../../../util/Format";
import { useActionReportMaintenance } from "../../../hooks/useAction";
import { useReportState } from "../../../hooks/useReportState";
import { convertValueToSearch, getDefaultFilters, getRelatedFilters } from "../../../utils";
import { BaseReportGrid } from "../../BaseReportGrid";

const renderOrigin = (booking: Booking) => {
  if (!booking) return "";

  return booking.origin;
};

const renderDestination = (booking: Booking) => {
  if (!booking) return "";

  return booking.destination;
};

const renderShipmentMode = (shipmentType: string) => {
  return <span>{capitalizeFirstLetter(shipmentType)}</span>;
};

export const BookingReport = () => {
  const { getAllBookings } = useActionReportMaintenance();
  const { currentUser, isSPVersion, isFMVersion } = useAppState();
  const { state } = useLocation<ReportProps>();
  const { t: translate } = useTranslation();
  const {
    filters,
    setFilters,
    tempFilters,
    setTempFilters,
    allCurrentFilters,
    dateFilters,
    setDateFilters,
  } = useReportState(false);

  useEffect(() => {
    if (state?.name) {
      setFilters((prev) => {
        return {
          ...prev,
          ...getDefaultFilters(state?.name || ""),
        };
      });
    }
  }, [state]);

  const columns: ColumnWithKey[] = [
    {
      name: "containerId",
      title: `${translate("booking")} #`,
      searchIdentifier: "container_id",
      formatComponent: (reference: string, booking: Booking) =>
        renderSharedReference(
          reference,
          currentUser?.companyId !== booking?.companyId,
          translate("booking")
        ),
      hideShowColumn: true,
    },
    {
      name: "shipmentType",
      title: translate("mode"),
      searchIdentifier: "shipmentType",
      formatComponent: (value: string) => value && renderShipmentMode(value),
      hideSearch: true,
    },
    ...(isSPVersion || isFMVersion
      ? [
          {
            name: "company",
            title: translate("supplier"),
            key: "supplierName",
            formatText: (company: Company) => company?.supplierAssociated?.name || "",
            searchIdentifier: "supplierAssociated.name",
          },
        ]
      : []),
    {
      name: "purchaseOrders",
      title: translate("orders"),
      formatComponent: (orders: PurchaseOrder[]) => {
        return (
          <MoreOptions
            options={orders?.map((order) => order.reference)}
            title={translate("orders")}
            manyOptionsToShow={1}
          />
        );
      },
      searchIdentifier: "purchaseOrders.purchase_order_id",
    },
    {
      name: "totalQuantity",
      title: translate("ssccQty"),
      hideSearch: true,
    },

    {
      name: "origin",
      title: translate("origin"),
      formatComponent: (value, row) => renderOrigin(row),
    },
    {
      name: "destination",
      title: translate("destination"),
      formatComponent: (value, row) => renderDestination(row),
    },
    {
      name: "statusShipment",
      title: translate("status"),
      formatComponent: (status: string) =>
        status && <BadgeBookingStatus value={capitalizeFirstLetterAllWorlds(status)} />,
      hideSearch: true,
    },
    {
      name: "createdAt",
      title: translate("createdAt"),
      formatText: (value) => formatDateWithoutTimeZone(value),
    },
  ];

  const filterCategories: Category[] = [
    {
      name: `${translate("booking")} #`,
      column: "id",
      onSearch: (value: string) =>
        ShipmentBookingService.getAllNetwork({
          companyId: currentUser?.companyId,
          isBooking: 1,
          group: ["shipment.id"],
          searchByOr: JSON.stringify(convertValueToSearch(value, "container_id")),
          include: JSON.stringify([]),
          attributes: ["id", "containerId"],
        }),
      getOptionLabel: (option: Booking) => option?.containerId || "",
    },
    {
      name: translate("mode"),
      column: "shipmentType",
      options: [
        { value: BookingModes.SEA, label: translate("sea") },
        { value: BookingModes.AIR, label: translate("air") },
        { value: BookingModes.ROAD, label: translate("road") },
      ],
      getOptionLabel: (option: Option) => option?.label || "",
      optionColumn: "value",
    },
    {
      name: translate("supplier"),
      column: "ownerCompanyId",
      onSearch: (value: string) =>
        SupplierService.getAll({
          companyId: currentUser?.companyId,
          searchByOr: JSON.stringify(convertValueToSearch(value, "name")),
          attributes: ["id", "name", "associatedCompanyId"],
          isCompany: true,
          include: JSON.stringify([{ association: "associatedCompany" }]),
        }),
      getOptionLabel: (option: Supplier) => option?.name || "",
      optionColumn: "associatedCompanyId",
    },
    {
      name: `${translate("order")} #`,
      column: "purchaseOrders.id",
      onSearch: (value: string) =>
        PurchaseOrderService.getAllNetwork({
          group: ["purchase_order.id"],
          companyId: currentUser?.companyId,
          include: JSON.stringify([]),
          attributes: ["id", "reference"],
          searchByOr: JSON.stringify(convertValueToSearch(value, "purchase_order_id")),
        }),
      getOptionLabel: (option: PurchaseOrder) => option?.reference || "",
    },
    {
      name: translate("origin"),
      column: "portOfLoadingId",
      onSearch: (value: string) =>
        PortService.getAll({
          searchByOr: JSON.stringify(convertValueToSearch(value, "site_code")),
        }),
      getOptionLabel: (option: Port) => option?.siteCode || "",
    },
    {
      name: translate("destination"),
      column: "portOfDischargeId",
      onSearch: (value: string) =>
        PortService.getAll({
          searchByOr: JSON.stringify(convertValueToSearch(value, "site_code")),
        }),
      getOptionLabel: (option: Port) => option?.siteCode || "",
    },
  ];

  const bookingReportInclude = [
    {
      association: "purchaseOrders",
      attributes: ["reference"],
      where: getRelatedFilters(allCurrentFilters, "purchaseOrders"),
    },
    {
      association: "company",
      attributes: ["id"],
      include: [{ association: "supplierAssociated", attributes: ["name"] }],
    },
  ];

  return (
    <BaseReportGrid
      getAllData={getAllBookings}
      columns={columns}
      dateFilters={dateFilters}
      setDateFilters={setDateFilters}
      filterCategories={filterCategories}
      filters={filters}
      setFilters={setFilters}
      reportInclude={bookingReportInclude}
      defaultFilters={getDefaultFilters(state?.name || "")}
      tempFilters={tempFilters}
      setTempFilters={setTempFilters}
      allCurrentFilters={allCurrentFilters}
      isDataLocal={false}
    />
  );
};
